import axiosInstance from "./axiosInstance";

export interface SubscriptionStatus {
  isActive: boolean;
  tier: "free" | "trial" | "explorer" | "expert" | "enterprise";
  trialEndDate?: string;
  subscriptionId?: string;
  status?: string;
}

export interface CreateStripeSubscriptionRequest {
  planId: "explorer" | "expert" | "enterprise";
  paymentMethodId: string;
  customerInfo: {
    email: string;
    name: string;
  };
}

export interface SubscriptionSummary {
  tier: "free" | "trial" | "explorer" | "expert" | "enterprise";
  isActive: boolean;
  hasAccess: {
    forum: boolean;
    soapvett: boolean;
    library: boolean;
    nala: boolean;
    clinicflow: boolean;
  };
  dailyLimits?: {
    nala: number; // -1 for unlimited
    soapvett: number; // -1 for unlimited
    library: number; // -1 for unlimited (bytes)
  };
  trialInfo?: {
    isInTrial: boolean;
    trialEndDate: string;
    daysRemaining: number;
  };
  subscription?: {
    id: string;
    status: string;
    currentPeriodEnd: string;
  };
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  currency: string;
  features: string[];
  maxConsultations: number;
  duration: string;
}

export interface Group {
  id: string;
  name: string;
  planId: string;
  veterinarians: number;
  totalPrice: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  members?: string[];
  // Admin and billing fields
  adminEmail?: string;
  adminUserId?: string;
  adminStatus?: "pending" | "active" | "none";
  /** Additional co-admins UIDs */
  adminUids?: string[];
  billing?: {
    paymentProvider?: "stripe" | "manual";
    manualPayment?: boolean;
    manualPaymentNotes?: string;
    cycle: "monthly" | "annual";
    startDate: string;
    nextBillingDate: string;
    status: "pending" | "pending_checkout" | "payment_required" | "trialing" | "active" | "cancelled";
    customPrice?: number | null;
    freeMonths?: number;
    freeAccessEndsAt?: string | null;
  };
  basePricePerVeterinarian?: number;
  pricePerVeterinarian?: number;
  discount?: {
    type: "none" | "percent" | "amount";
    value: number;
    appliedPerVeterinarian?: number;
  };
  invitationToken?: string;
  invitationExpiresAt?: Date | string;
  couponCode?: string;
  country?: string;
  timezone?: string;
  clinic?: {
    name?: string;
    legalName?: string;
    taxId?: string;
    phone?: string;
    website?: string;
    address?: {
      line1?: string;
      line2?: string;
      city?: string;
      state?: string;
      postalCode?: string;
      country?: string;
    };
  };
}

export interface Subscription {
  id: string;
  userId: string;
  planId: string;
  groupId?: string;
  status: "active" | "inactive" | "cancelled" | "pending" | "expired";
  paymentStatus?: "paid" | "pending" | "failed";
  amount: number;
  startDate: string;
  endDate?: string;
  createdAt: string;
  updatedAt: string;
  plan?: SubscriptionPlan;
  group?: Group;
  userEmail?: string;
  userName?: string;
}

class SubscriptionService {
  /**
   * Get user's current subscription
   */
  async getUserSubscription(): Promise<Subscription | null> {
    try {
      const response = await axiosInstance.get(
        "/api/subscriptions/my-subscription",
      );
      return response.data.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Create a new subscription
   */
  async createSubscription(
    planId: string,
    groupId?: string,
  ): Promise<Subscription> {
    const response = await axiosInstance.post("/api/subscriptions", {
      planId,
      groupId,
    });
    return response.data.data;
  }

  /**
   * Update an existing subscription
   */
  async updateSubscription(
    subscriptionId: string,
    updateData: Partial<Subscription>,
  ): Promise<Subscription> {
    const response = await axiosInstance.put(
      `/api/subscriptions/${subscriptionId}`,
      updateData,
    );
    return response.data.data;
  }

  /**
   * Get all available subscription plans
   */
  async getAvailablePlans(): Promise<SubscriptionPlan[]> {
    const response = await axiosInstance.get("/api/subscriptions/plans");

    return response.data.data;
  }

  /**
   * Create a new group (admin only)
   */
  async createGroup(groupData: {
    name: string;
    planId: string;
    veterinarians: number;
    totalPrice: number;
    adminEmail?: string;
    billingCycle?: "monthly" | "annual";
    billingStartDate?: string;
    customPrice?: number | null;
    currency?: string;
    basePricePerVeterinarian?: number;
    discountType?: "none" | "percent" | "amount";
    discountValue?: number;
    freeMonths?: number;
    paymentProvider?: "stripe" | "manual";
    country?: string;
    timezone?: string;
    clinic?: Group["clinic"];
    billingAddress?: {
      line1?: string;
      line2?: string;
      city?: string;
      state?: string;
      postalCode?: string;
      country?: string;
    };
  }): Promise<Group> {
    const response = await axiosInstance.post(
      "/api/subscriptions/groups",
      groupData,
    );
    return response.data.data;
  }

  /**
   * Resend group admin invitation (admin only)
   */
  async resendGroupInvitation(groupId: string): Promise<void> {
    await axiosInstance.post(`/api/collective/${groupId}/resend-invitation`);
  }

  async createGroupCheckout(groupId: string): Promise<{ checkoutUrl: string; checkoutSessionId: string }> {
    const response = await axiosInstance.post(`/api/collective/${groupId}/checkout`);
    return response.data;
  }

  /**
   * Add user to group (admin only)
   */
  async addUserToGroup(userId: string, groupId: string): Promise<void> {
    await axiosInstance.post("/api/subscriptions/groups/add-user", {
      userId,
      groupId,
    });
  }

  /**
   * Set user plan directly (admin only)
   */
  async adminSetUserPlan(
    targetUserId: string,
    planType: string,
  ): Promise<void> {
    await axiosInstance.patch("/api/subscriptions/admin/user/update-plan", {
      targetUserId,
      planType,
      subscriptionStatus: "active",
    });
  }

  /**
   * Sync all group members to enterprise plan (admin only)
   */
  async syncGroupMembersToEnterprise(): Promise<{
    fixed: number;
    total: number;
    users: { uid: string; email: string; oldPlan: string }[];
  }> {
    const response = await axiosInstance.post(
      "/api/subscriptions/admin/groups/sync-members",
    );
    return response.data.data;
  }

  /**
   * Remove user from group (admin only)
   */
  async removeUserFromGroup(userId: string, groupId: string): Promise<void> {
    await axiosInstance.post("/api/subscriptions/groups/remove-user", {
      userId,
      groupId,
    });
  }

  /**
   * Get all groups (admin only)
   */
  async getAllGroups(): Promise<Group[]> {
    const response = await axiosInstance.get("/api/subscriptions/groups");
    return response.data.data;
  }

  /**
   * Get specific group information
   */
  async getGroup(groupId: string): Promise<Group> {
    const response = await axiosInstance.get(
      `/api/subscriptions/groups/${groupId}`,
    );
    return response.data.data;
  }

  /**
   * Update group information (admin only)
   */
  async updateGroup(
    groupId: string,
    updateData: {
      name?: string;
      planId?: string;
      veterinarians?: number;
      totalPrice?: number;
      paymentProvider?: "stripe" | "manual";
      billingStatus?: "active" | "pending_checkout" | "payment_required" | "trialing" | "cancelled" | "suspended";
      manualPaymentNotes?: string;
    },
  ): Promise<Group> {
    const response = await axiosInstance.put(
      `/api/subscriptions/groups/${groupId}`,
      updateData,
    );
    return response.data.data;
  }

  /**
   * Delete a group (admin only)
   */
  async deleteGroup(groupId: string): Promise<void> {
    await axiosInstance.delete(`/api/subscriptions/groups/${groupId}`);
  }

  /**
   * Update the admin(s) of a group (admin only).
   * @param groupId - Group ID
   * @param data.primaryAdminUserId - UID of the new primary admin (pass null to clear)
   * @param data.adminUids - Full list of co-admin UIDs
   */
  async updateGroupAdmins(
    groupId: string,
    data: { primaryAdminUserId?: string | null; adminUids?: string[] },
  ): Promise<Group> {
    const response = await axiosInstance.patch(
      `/api/subscriptions/groups/${groupId}/admins`,
      data,
    );
    return response.data.data;
  }

  /**
   * Get predefined subscription plans with VettConsult pricing
   * Note: This method is deprecated. Use getAvailablePlans() instead to get current pricing from backend.
   */
  getPredefinedPlans(): SubscriptionPlan[] {
    return [
      {
        id: "explorer",
        name: "Explorer Plan",
        price: 0,
        currency: "USD",
        duration: "monthly",
        maxConsultations: 2,
        features: [
          "AI with access to our clinical case database",
          "Templates",
          "Forum",
          "2 private consultations with messaging follow-up",
        ],
      },
      {
        id: "expert",
        name: "Expert Plan",
        price: 40,
        currency: "USD",
        duration: "monthly",
        maxConsultations: 4,
        features: [
          "AI with access to our clinical case database",
          "Templates",
          "Forum",
          "4 private consultations with messaging follow-up",
          "Share your account with another veterinarian",
          "1 extra private consultation",
        ],
      },
      {
        id: "enterprise",
        name: "Enterprise Plan",
        price: 0,
        currency: "USD",
        duration: "custom",
        maxConsultations: -1,
        features: [
          "All Expert Plan features",
          "Custom pricing based on team size",
          "Dedicated support",
          "Advanced analytics",
          "Custom integrations",
          "Unlimited consultations",
        ],
      },
      {
        id: "premium",
        name: "Premium Plan (Legacy)",
        price: 0,
        currency: "USD",
        duration: "monthly",
        maxConsultations: 5,
        features: [
          "AI with access to clinical case database",
          "Clinical templates",
          "Community forum access",
          "4 private consultations with messaging follow-up",
          "Share your account with another veterinarian",
          "1 extra private consultation",
        ],
      },
    ];
  }

  /**
   * Get all subscriptions (admin only)
   */
  async getAllSubscriptions(): Promise<Subscription[]> {
    const response = await axiosInstance.get("/api/subscriptions/admin/all");
    return response.data.data;
  }

  /**
   * Mark subscription as paid (admin only)
   */
  async markSubscriptionAsPaid(subscriptionId: string): Promise<Subscription> {
    const response = await axiosInstance.patch(
      `/api/subscriptions/admin/${subscriptionId}/mark-paid`,
    );
    return response.data.data;
  }

  /**
   * Create subscription for specific user (admin only)
   */
  async createSubscriptionForUser(
    userEmail: string,
    planId: string,
    adminNotes?: string,
  ): Promise<Subscription> {
    try {
      const usersResponse = await axiosInstance.get("/profile/profiles");
      const users = usersResponse.data;
      const targetUser = users.find((user: any) => user.email === userEmail);

      if (targetUser && targetUser.uid) {
        const response = await axiosInstance.patch(
          "/api/subscriptions/admin/user/update-plan",
          {
            targetUserId: targetUser.uid,
            planType: planId,
            subscriptionStatus: "active",
          },
        );
        return response.data.data;
      } else {
      }
    } catch (error: any) {
      console.error("Error getting userId from email:", error);
    }

    try {
      const response = await axiosInstance.patch(
        "/api/subscriptions/admin/user/update-plan",
        {
          targetUserId: userEmail,
          planType: planId,
          subscriptionStatus: "active",
        },
      );
      return response.data.data;
    } catch (error: any) {
      console.error(
        "Error with email as userId, trying original format:",
        error,
      );

      // Método 3: Formato original como último recurso
      const response = await axiosInstance.patch(
        "/api/subscriptions/admin/user/update-plan",
        {
          userEmail,
          planId,
          planType: planId,
          subscriptionStatus: "active",
          adminNotes: adminNotes || `Admin changed plan to ${planId}`,
        },
      );
      return response.data.data;
    }
  }

  /**
   * Grant access to user (admin only) - Alternative method
   */
  async adminGrantAccess(
    userEmail: string,
    planId: string,
    duration?: string,
  ): Promise<any> {
    try {
      // Buscar userId a través de la lista de usuarios
      const usersResponse = await axiosInstance.get("/profile/profiles");
      const users = usersResponse.data;
      const targetUser = users.find((user: any) => user.email === userEmail);

      if (targetUser && targetUser.uid) {
        const response = await axiosInstance.post(
          "/api/subscriptions/admin/user/grant-access",
          {
            targetUserId: targetUser.uid,
            planType: planId,
            subscriptionStatus: "active",
            duration: duration || "monthly",
          },
        );
        return response.data;
      } else {
      }
    } catch (error: any) {
      console.error("Error getting userId from email:", error);
    }

    // Fallback: intentar con email como userId
    try {
      const response = await axiosInstance.post(
        "/api/subscriptions/admin/user/grant-access",
        {
          targetUserId: userEmail,
          planType: planId,
          subscriptionStatus: "active",
          duration: duration || "monthly",
        },
      );
      return response.data;
    } catch (error: any) {
      console.error(
        "Error with email as userId, trying original format:",
        error,
      );

      // Último recurso: formato original
      const response = await axiosInstance.post(
        "/api/subscriptions/admin/user/grant-access",
        {
          userEmail,
          planId,
          planType: planId,
          subscriptionStatus: "active",
          duration: duration || "monthly",
        },
      );
      return response.data;
    }
  }

  // ✅ STRIPE SUBSCRIPTION METHODS

  /**
   * Get current user's Stripe subscription status
   */
  async getStripeSubscriptionStatus(): Promise<SubscriptionSummary> {
    try {
      const response = await axiosInstance.get("/api/subscriptions/status");
      return response.data;
    } catch (error) {
      console.error("Error fetching Stripe subscription status:", error);
      throw error;
    }
  }

  /**
   * Create a new Stripe subscription
   */
  async createStripeSubscription(
    data: CreateStripeSubscriptionRequest,
  ): Promise<any> {
    try {
      const response = await axiosInstance.post(
        "/api/subscriptions/create-subscription",
        data,
      );
      return response.data;
    } catch (error) {
      console.error("Error creating Stripe subscription:", error);
      throw error;
    }
  }

  /**
   * Cancel current Stripe subscription
   */
  async cancelStripeSubscription(): Promise<void> {
    try {
      await axiosInstance.post("/api/subscriptions/stripe/cancel");
    } catch (error) {
      console.error("Error canceling Stripe subscription:", error);
      throw error;
    }
  }

  /**
   * Change Stripe subscription plan
   */
  async changeStripeSubscriptionPlan(newPlanId: string): Promise<void> {
    try {
      await axiosInstance.post("/api/subscriptions/change-plan", {
        newPlanId,
      });
    } catch (error) {
      console.error("Error changing Stripe subscription plan:", error);
      throw error;
    }
  }

  /**
   * Downgrade to Explorer (free) plan
   */
  async downgradeToExplorer(): Promise<void> {
    try {
      await axiosInstance.post("/api/subscriptions/downgrade-to-explorer");
    } catch (error) {
      console.error("Error downgrading to Explorer:", error);
      throw error;
    }
  }

  /**
   * Create Stripe checkout session for plan upgrade
   * @param planId - Plan ID to subscribe to
   * @param billingInterval - 'monthly' or 'annual' (default: 'monthly')
   * @param currency - 'usd' or 'cad' (default: 'usd')
   */
  async createStripeCheckoutSession(
    planId: string,
    billingInterval: "monthly" | "annual" = "monthly",
    currency: string = "usd",
  ): Promise<{ url: string }> {
    try {
      const response = await axiosInstance.post(
        "/api/subscriptions/create-checkout-session",
        {
          planId,
          billingInterval,
          currency,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error creating Stripe checkout session:", error);
      throw error;
    }
  }

  /**
   * Resume a canceled Stripe subscription
   */
  async resumeStripeSubscription(): Promise<void> {
    try {
      await axiosInstance.post("/api/subscriptions/resume-subscription");
    } catch (error) {
      console.error("Error resuming Stripe subscription:", error);
      throw error;
    }
  }

  /**
   * Get any user's billing history from Stripe (admin only)
   */
  async adminGetUserBillingHistory(userId: string): Promise<any> {
    try {
      const response = await axiosInstance.get(
        `/api/subscriptions/admin/user/${userId}/billing-history`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching admin billing history:", error);
      throw error;
    }
  }

  /**
   * Get user's billing history from Stripe
   */
  async getStripeBillingHistory(): Promise<any> {
    try {
      const response = await axiosInstance.get(
        "/api/subscriptions/billing-history",
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching billing history:", error);
      throw error;
    }
  }

  /**
   * Get upcoming invoice from Stripe
   */
  async getUpcomingInvoice(): Promise<any> {
    try {
      const response = await axiosInstance.get(
        "/api/subscriptions/upcoming-invoice",
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching upcoming invoice:", error);
      throw error;
    }
  }

  /**
   * Download invoice PDF
   */
  async downloadInvoice(invoiceId: string): Promise<Blob> {
    try {
      const response = await axiosInstance.get(
        `/api/subscriptions/invoice/${invoiceId}/pdf`,
        {
          responseType: "blob",
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error downloading invoice:", error);
      throw error;
    }
  }

  /**
   * Get current payment method details
   */
  async getCurrentPaymentMethod(): Promise<any> {
    try {
      const response = await axiosInstance.get(
        "/api/subscriptions/payment-method",
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching payment method:", error);
      throw error;
    }
  }

  /**
   * Create setup intent for updating payment method
   */
  async createSetupIntent(): Promise<{ client_secret: string }> {
    try {
      const response = await axiosInstance.post(
        "/api/subscriptions/create-setup-intent",
      );
      return response.data;
    } catch (error) {
      console.error("Error creating setup intent:", error);
      throw error;
    }
  }

  /**
   * Update payment method for current subscription
   */
  async updatePaymentMethodForSubscription(
    paymentMethodId: string,
  ): Promise<void> {
    try {
      await axiosInstance.post(
        "/api/subscriptions/update-subscription-payment-method",
        {
          paymentMethodId,
        },
      );
    } catch (error) {
      console.error("Error updating subscription payment method:", error);
      throw error;
    }
  }

  /**
   * Change subscription plan (handles both upgrades and downgrades)
   * @param newPlanId - Plan to change to
   * @param billingInterval - 'monthly' or 'annual' (default: 'monthly')
   */
  async changePlan(
    newPlanId: string,
    billingInterval: "monthly" | "annual" = "monthly",
  ): Promise<any> {
    try {
      const response = await axiosInstance.post(
        "/api/subscriptions/change-plan",
        {
          newPlanId,
          reason: "user_requested",
          billingInterval,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error changing plan:", error);
      throw error;
    }
  }

  /**
   * Update payment method for Stripe subscription
   */
  async updateStripePaymentMethod(paymentMethodId: string): Promise<void> {
    try {
      await axiosInstance.post("/api/subscriptions/update-payment-method", {
        paymentMethodId,
      });
    } catch (error) {
      console.error("Error updating Stripe payment method:", error);
      throw error;
    }
  }

  /**
   * Create payment intent for Stripe subscription
   */
  async createStripePaymentIntent(
    planId: string,
  ): Promise<{ clientSecret: string }> {
    try {
      const response = await axiosInstance.post(
        "/api/subscriptions/create-payment-intent",
        {
          planId,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error creating Stripe payment intent:", error);
      throw error;
    }
  }

  /**
   * Check if user has access to specific service
   */
  async checkServiceAccess(
    service: string,
  ): Promise<{ hasAccess: boolean; reason?: string }> {
    try {
      const response = await axiosInstance.get(
        `/api/subscriptions/check-access/${service}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error checking service access:", error);
      return { hasAccess: false, reason: "Error checking access" };
    }
  }

  /**
   * Get comprehensive subscription status including Stripe and manual data
   */
  async getSubscriptionStatus(): Promise<any> {
    try {
      const response = await axiosInstance.get("/api/subscriptions/status");
      return response.data;
    } catch (error) {
      console.error("Error fetching subscription status:", error);
      throw error;
    }
  }

  /**
   * Process Firestore date/timestamp to ISO string
   */
  private processFirestoreDate(date: any): string | null {
    if (!date) return null;

    try {
      // Firestore Timestamp object (admin SDK) often has toDate()
      if (typeof date === "object" && typeof date.toDate === "function") {
        return date.toDate().toISOString();
      }

      // Firestore Timestamp object
      if (typeof date === "object" && (date.seconds || date._seconds)) {
        const seconds = date.seconds || date._seconds;
        return new Date(seconds * 1000).toISOString();
      }

      // Already a string (ISO format)
      if (typeof date === "string") {
        return date;
      }

      // JavaScript Date object
      if (date instanceof Date) {
        return date.toISOString();
      }

      // Fallback: try to parse as timestamp
      const timestamp = parseInt(date);
      if (!isNaN(timestamp)) {
        return new Date(timestamp).toISOString();
      }
    } catch (error) {
      console.error("Error processing date:", date, error);
    }

    return null;
  }

  /**
   * Get complete subscription details combining user profile and subscription data
   */
  async getCompleteSubscriptionDetails(): Promise<any> {
    try {
      // Fetching subscription details

      // Obtener solo datos que el cliente puede acceder
      const [userProfileResponse, subscriptionStatusResponse, plansResponse] =
        await Promise.all([
          axiosInstance.get("/profile/profile"), // Datos del perfil del usuario actual
          axiosInstance.get("/api/subscriptions/status"), // Estado de suscripción básico
          axiosInstance.get("/api/subscriptions/plans"), // Planes disponibles
        ]);

      const userProfile = userProfileResponse.data;
      // Backend returns { success, data }, but some callers may return raw.
      const subscriptionStatus =
        subscriptionStatusResponse.data?.data ??
        subscriptionStatusResponse.data;
      const plans = plansResponse.data?.data ?? [];

      // Data successfully fetched

      // Para usuarios normales, usar datos del perfil y status directamente
      // No podemos acceder a la lista completa de suscripciones sin permisos de admin

      // Determinar el plan actual (prioridad: status > datos del usuario > por defecto)
      let planType =
        subscriptionStatus.planType ||
        userProfile.planType ||
        userProfile.subscriptionTier ||
        "explorer";
      let subscriptionStatusValue =
        subscriptionStatus.subscriptionStatus ||
        userProfile.subscriptionStatus ||
        "active";

      // Stripe fields (if present)
      const stripeCurrentPeriodEnd = this.processFirestoreDate(
        subscriptionStatus.stripeCurrentPeriodEnd ||
          subscriptionStatus.subscription?.currentPeriodEnd ||
          subscriptionStatus.currentPeriodEnd,
      );

      // Plan type and status determined

      // Process dates and plan information

      // Buscar información del plan
      const planInfo = plans.find((plan: any) => plan.id === planType);

      // Build final response object

      const finalResponse = {
        // Datos combinados
        planType: planType,
        subscriptionStatus: subscriptionStatusValue,
        subscriptionTier: planType,
        isActive:
          subscriptionStatusValue === "active" ||
          subscriptionStatusValue === "trialing",

        // Stripe subscription data (for UI messaging like cancellation-at-period-end)
        isStripeUser: !!subscriptionStatus.isStripeUser,
        stripeSubscriptionId: subscriptionStatus.stripeSubscriptionId,
        stripePlanId: subscriptionStatus.stripePlanId,
        stripeStatus: subscriptionStatus.stripeStatus,
        stripeCurrentPeriodEnd,
        stripeCancelAtPeriodEnd: !!subscriptionStatus.stripeCancelAtPeriodEnd,

        // Información del plan
        planName:
          planInfo?.name ||
          `${planType.charAt(0).toUpperCase() + planType.slice(1)} Plan`,
        planFeatures: planInfo?.features || [],
        planPrice: planInfo?.price || 0,
        planCurrency: planInfo?.currency || "USD",

        // Datos del usuario
        userEmail: userProfile.email,
        userName:
          userProfile.displayName ||
          userProfile.firstName + " " + userProfile.lastName,

        // Datos de suscripción desde perfil del usuario
        subscriptionId:
          subscriptionStatus.subscriptionId || `user-${userProfile.uid}`,
        startDate: this.processFirestoreDate(
          subscriptionStatus.memberSince ||
            subscriptionStatus.createdAt ||
            userProfile.createdAt,
        ),
        paidAt: this.processFirestoreDate(subscriptionStatus.paidAt),

        // Información de consultas (si existe)
        consultationsIncluded: subscriptionStatus.consultationsIncluded || 0,
        consultationsUsed: subscriptionStatus.consultationsUsed || 0,

        // Información adicional
        currency: subscriptionStatus.currency || planInfo?.currency || "USD",
        price: subscriptionStatus.price || planInfo?.price || 0,
        paymentStatus: subscriptionStatus.paymentStatus,

        // Group/Manual payment info (if present)
        groupId: subscriptionStatus.groupId || null,
        groupName: subscriptionStatus.groupName || null,
        manualPayment: !!subscriptionStatus.manualPayment,
        paymentNotes: subscriptionStatus.paymentNotes || null,

        // Admin info (if present)
        isAdmin: !!subscriptionStatus.isAdmin,
        adminNotes: subscriptionStatus.adminNotes || null,

        // Estado original para debugging
        originalUserData: userProfile,
        originalStatus: subscriptionStatus,
      };

      // Return final subscription data

      return finalResponse;
    } catch (error) {
      console.error("Error fetching complete subscription details:", error);
      throw error;
    }
  }

  /**
   * Start free trial for user
   */
  async startTrial(planId: string = "expert"): Promise<any> {
    try {
      const response = await axiosInstance.post(
        "/api/subscriptions/start-trial",
        {
          planId,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error starting trial:", error);
      throw error;
    }
  }

  /**
   * Create subscription with trial period and payment method setup
   */
  async createSubscriptionWithTrial(planId: string = "expert"): Promise<any> {
    try {
      const response = await axiosInstance.post(
        "/api/subscriptions/create-with-trial",
        {
          planId,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error creating subscription with trial:", error);
      throw error;
    }
  }

  /**
   * Sync subscription data from Stripe
   */
  async syncSubscription(): Promise<any> {
    try {
      const response = await axiosInstance.post("/api/subscriptions/sync");
      return response.data;
    } catch (error) {
      console.error("Error syncing subscription:", error);
      throw error;
    }
  }

  /**
   * Force sync subscription data from Stripe (enhanced method)
   */
  async forceSyncSubscription(): Promise<any> {
    try {
      const response = await axiosInstance.post(
        "/api/subscriptions/force-sync",
      );
      return response.data;
    } catch (error) {
      console.error("Error force syncing subscription:", error);
      throw error;
    }
  }

  /**
   * Extend trial with payment method (Phase 2 of hybrid trial)
   */
  async extendTrialWithCard(planId: string = "expert"): Promise<any> {
    try {
      const response = await axiosInstance.post(
        "/api/subscriptions/extend-trial",
        {
          planId,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error extending trial:", error);
      throw error;
    }
  }

  // ---- Specialist Group Methods ----

  /**
   * Get specialists assigned to a group
   */
  async getGroupSpecialists(groupId: string): Promise<
    {
      uid: string;
      firstName: string;
      lastName: string;
      displayName: string;
      email: string;
      specialty: string;
      profileImage: string | null;
    }[]
  > {
    const response = await axiosInstance.get(
      `/api/collective/${groupId}/specialists`,
    );
    return response.data.specialists;
  }

  /**
   * Assign a specialist to a group (admin only)
   */
  async addSpecialistToGroup(
    specialistId: string,
    groupId: string,
  ): Promise<void> {
    await axiosInstance.post(`/api/collective/${groupId}/specialists`, {
      specialistId,
    });
  }

  /**
   * Remove a specialist from a group (admin only)
   */
  async removeSpecialistFromGroup(
    specialistId: string,
    groupId: string,
  ): Promise<void> {
    await axiosInstance.delete(
      `/api/collective/${groupId}/specialists/${specialistId}`,
    );
  }
}

const subscriptionService = new SubscriptionService();
export default subscriptionService;
